import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Navbar, Footer } from '../components/Layout';
import { CoverFrontPanel } from '../components/CoverFrontPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AdminFeedbackTab } from '../components/AdminFeedbackTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { CoverTemplateEditor } from '../components/CoverTemplateEditor';
import { NotificationTemplatesPanel } from '../components/NotificationTemplatesPanel';
import { ClientFormTemplatesPanel } from '../components/ClientFormTemplatesPanel';
import { AdminBillingPanel } from '../components/AdminBillingPanel';
import { AdminPaymentsTab } from '../components/AdminPaymentsTab';
import AdminHelpPanel from '../components/AdminHelpPanel';
import AdminCostsPanel from '../components/AdminCostsPanel';
import { PrintPreparationDialog } from '../components/PrintPreparationDialog';
import { WaitlistDialog } from '../components/WaitlistDialog';
import { WhitelistedIpsDialog } from '../components/WhitelistedIpsDialog';
import { 
  Users, BookOpen, Package, TrendingUp, Search,
  MoreVertical, Eye, Trash2, Shield, ShieldOff,
  Mail, Phone, Calendar, AlertTriangle,
  RefreshCw, Briefcase, UserCheck, Settings, ArrowRightLeft,
  UserPlus, Pencil, Lock, Eye as EyeIcon, EyeOff,
  Clock, CheckCircle2, AlertCircle, Timer, Download,
  FileText, Copy, Star, Plus, Camera, Upload, Loader2, ClipboardList, Receipt, HelpCircle, Sparkles, ArrowUpCircle, MessageSquareHeart,
  Printer, ChevronRight, MailQuestion, Euro,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_PERMISSIONS = [
  { key: 'manage_users', icon: Users },
  { key: 'manage_books', icon: BookOpen },
  { key: 'manage_orders', icon: Package },
  { key: 'manage_contributions', icon: Settings },
  { key: 'view_stats', icon: TrendingUp },
];

const BOOK_STATUSES = [
  { key: 'alta', color: 'bg-slate-100 text-slate-700' },
  { key: 'recopilando', color: 'bg-blue-100 text-blue-700' },
  { key: 'generando', color: 'bg-amber-100 text-amber-700' },
  { key: 'en_revision', color: 'bg-orange-100 text-orange-700' },
  { key: 'preparacion_imprenta', color: 'bg-teal-100 text-teal-700' },
  { key: 'envio_imprenta', color: 'bg-purple-100 text-purple-700' },
  { key: 'envio_cliente', color: 'bg-cyan-100 text-cyan-700' },
  { key: 'recibido_cliente', color: 'bg-green-100 text-green-700' },
];

const UserTable = ({ users, searchTerm, onSearch, onViewDetail, onEditUser, onToggleAdmin, onDelete, onChangeRole, onManagePermissions, currentUserId, t, showRoleSwitch }) => {
  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.phone?.includes(searchTerm)
  );

  return (
    <>
      <div className="relative w-full md:w-80 mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={t('admin.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => onSearch(e.target.value)}
          className="pl-10"
          data-testid="search-users"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('admin.user')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('admin.contact')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('admin.books')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('admin.role')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('admin.registered')}</th>
              <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((userItem) => (
              <tr key={userItem.user_id} className="border-b hover:bg-muted/50" data-testid={`user-row-${userItem.user_id}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {userItem.picture ? (
                        <img src={userItem.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <span className="font-semibold text-primary text-sm">
                          {userItem.name?.charAt(0) || 'U'}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{userItem.name || t('admin.noName')}</p>
                      <p className="text-xs text-muted-foreground">{userItem.user_id}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm">
                    <p className="flex items-center gap-1"><Mail className="w-3 h-3" />{userItem.email}</p>
                    {userItem.phone && (
                      <p className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{userItem.phone}</p>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <Badge variant="outline">{userItem.books_count}</Badge>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-1.5">
                    {userItem.is_admin && <Badge className="bg-primary text-white">Admin</Badge>}
                    {userItem.role === 'employee' ? (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">{t('admin.employee')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('admin.client')}</Badge>
                    )}
                    {userItem.permissions?.length > 0 && (
                      <span className="text-xs text-muted-foreground">({userItem.permissions.length})</span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    {new Date(userItem.created_at).toLocaleDateString('es')}
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onViewDetail(userItem.user_id)}
                      data-testid={`view-detail-${userItem.user_id}`}
                      title={t('admin.viewDetails')}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewDetail(userItem.user_id)}>
                        <Eye className="w-4 h-4 mr-2" />{t('admin.viewDetails')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEditUser(userItem)}>
                        <Pencil className="w-4 h-4 mr-2" />{t('admin.editUser')}
                      </DropdownMenuItem>
                      {showRoleSwitch && (
                        <DropdownMenuItem onClick={() => onChangeRole(userItem)}>
                          <ArrowRightLeft className="w-4 h-4 mr-2" />
                          {userItem.role === 'employee' ? t('admin.convertToClient') : t('admin.convertToEmployee')}
                        </DropdownMenuItem>
                      )}
                      {userItem.role === 'employee' && (
                        <DropdownMenuItem onClick={() => onManagePermissions(userItem)}>
                          <Settings className="w-4 h-4 mr-2" />{t('admin.managePermissions')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => onToggleAdmin(userItem.user_id)}>
                        {userItem.is_admin ? (
                          <><ShieldOff className="w-4 h-4 mr-2" />{t('admin.removeAdmin')}</>
                        ) : (
                          <><Shield className="w-4 h-4 mr-2" />{t('admin.makeAdmin')}</>
                        )}
                      </DropdownMenuItem>
                      {userItem.user_id !== currentUserId && (
                        <DropdownMenuItem onClick={() => onDelete(userItem)} className="text-red-600 focus:text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />{t('admin.deleteUser')}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">{t('admin.noUsersFound')}</div>
        )}
      </div>
    </>
  );
};

const UserForm = ({ formData, setFormData, showPassword, setShowPassword, toggleFormPermission, t, isCreate }) => {
  const toggleFormPhase = (phase) => {
    setFormData(prev => ({
      ...prev,
      default_phases: prev.default_phases.includes(phase)
        ? prev.default_phases.filter(p => p !== phase)
        : [...prev.default_phases, phase]
    }));
  };

  return (
  <div className="space-y-4 py-2">
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="input-label">{t('auth.firstName')} *</Label>
        <Input
          value={formData.first_name}
          onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
          data-testid="form-first-name"
        />
      </div>
      <div>
        <Label className="input-label">{t('auth.lastName')}</Label>
        <Input
          value={formData.last_name}
          onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
          data-testid="form-last-name"
        />
      </div>
    </div>
    <div>
      <Label className="input-label">
        <Mail className="w-3.5 h-3.5 inline mr-1" />{t('auth.email')} *
      </Label>
      <Input
        type="email"
        value={formData.email}
        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
        data-testid="form-email"
      />
    </div>
    <div>
      <Label className="input-label">
        <Phone className="w-3.5 h-3.5 inline mr-1" />{t('auth.phone')}
      </Label>
      <Input
        value={formData.phone}
        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
        data-testid="form-phone"
      />
    </div>
    <div>
      <Label className="input-label">
        <Lock className="w-3.5 h-3.5 inline mr-1" />{t('auth.password')} {isCreate ? '*' : `(${t('admin.leaveBlankKeep')})`}
      </Label>
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
          className="pr-10"
          placeholder={isCreate ? '' : '••••••'}
          data-testid="form-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
        </button>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="input-label">{t('admin.role')}</Label>
        <Select value={formData.role} onValueChange={(v) => setFormData(prev => ({ ...prev, role: v, permissions: v === 'client' ? [] : prev.permissions }))}>
          <SelectTrigger data-testid="form-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="client">{t('admin.client')}</SelectItem>
            <SelectItem value="employee">{t('admin.employee')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end pb-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="form-admin"
            checked={formData.is_admin}
            onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_admin: !!v }))}
            data-testid="form-is-admin"
          />
          <Label htmlFor="form-admin" className="cursor-pointer flex items-center gap-1">
            <Shield className="w-3.5 h-3.5" /> Admin
          </Label>
        </div>
      </div>
    </div>
    {formData.role === 'employee' && (
      <div>
        <Label className="input-label mb-2 block">{t('admin.managePermissions')}</Label>
        <div className="space-y-2">
          {AVAILABLE_PERMISSIONS.map(({ key, icon: Icon }) => (
            <div key={key} className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-muted/50 transition-colors">
              <Checkbox
                id={`form-perm-${key}`}
                checked={formData.permissions.includes(key)}
                onCheckedChange={() => toggleFormPermission(key)}
                data-testid={`form-perm-${key}`}
              />
              <Icon className="w-4 h-4 text-muted-foreground" />
              <Label htmlFor={`form-perm-${key}`} className="flex-1 cursor-pointer text-sm">
                {t(`admin.perm_${key}`)}
              </Label>
            </div>
          ))}
        </div>
      </div>
    )}
    {formData.role === 'employee' && (
      <div>
        <Label className="input-label mb-2 block">{t('admin.defaultPhases')}</Label>
        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
          {BOOK_STATUSES.map(({ key, color }) => (
            <div key={key} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-muted/50 transition-colors">
              <Checkbox
                id={`form-phase-${key}`}
                checked={formData.default_phases.includes(key)}
                onCheckedChange={() => toggleFormPhase(key)}
                data-testid={`form-phase-${key}`}
              />
              <Label htmlFor={`form-phase-${key}`} className="cursor-pointer text-xs flex-1">
                <Badge className={`${color} text-xs`}>{t(`admin.bookStatus.${key}`)}</Badge>
              </Label>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);
};

const FIELD_TYPES = [
  { value: 'text', label: 'Texto corto' },
  { value: 'textarea', label: 'Texto largo' },
  { value: 'select', label: 'Selector' },
  { value: 'radio', label: 'Opciones' },
  { value: 'slider', label: 'Escala' },
];

const TemplateFieldEditor = ({ field, onChange, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const updateField = (key, value) => onChange({ ...field, [key]: value });

  const addOption = () => {
    const opts = field.options || [];
    updateField('options', [...opts, { value: `opt_${opts.length + 1}`, label: '' }]);
  };

  return (
    <div className="border rounded-lg p-3 bg-card" data-testid={`tmpl-field-${field.id}`}>
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium truncate">{field.label || 'Sin nombre'}</span>
          <span className="text-xs text-muted-foreground ml-2">
            ({FIELD_TYPES.find(t => t.value === field.type)?.label || field.type})
          </span>
          {field.required && <span className="text-xs text-red-500 ml-1">*</span>}
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          <Trash2 className="w-3.5 h-3.5 text-red-400" />
        </Button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-3 pl-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Etiqueta</Label>
              <Input value={field.label} onChange={(e) => updateField('label', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={field.type} onValueChange={(v) => updateField('type', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Placeholder</Label>
            <Input value={field.placeholder || ''} onChange={(e) => updateField('placeholder', e.target.value)} className="h-8 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={field.required} onCheckedChange={(v) => updateField('required', v)} />
            <Label className="text-xs">Obligatorio</Label>
          </div>
          {(field.type === 'select' || field.type === 'radio') && (
            <div>
              <Label className="text-xs mb-1 block">Opciones</Label>
              <div className="space-y-1">
                {(field.options || []).map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input value={opt.label} onChange={(e) => {
                      const opts = [...(field.options || [])];
                      opts[idx] = { ...opts[idx], label: e.target.value };
                      updateField('options', opts);
                    }} className="h-7 text-xs flex-1" placeholder="Etiqueta" />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                      const opts = [...(field.options || [])];
                      opts.splice(idx, 1);
                      updateField('options', opts);
                    }}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={addOption}>
                  <Plus className="w-3 h-3" /> Opcion
                </Button>
              </div>
            </div>
          )}
          {field.type === 'slider' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Min</Label>
                <Input type="number" value={field.min_val || 1} onChange={(e) => updateField('min_val', parseInt(e.target.value))} className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Max</Label>
                <Input type="number" value={field.max_val || 5} onChange={(e) => updateField('max_val', parseInt(e.target.value))} className="h-8 text-sm" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TemplateSection = ({ section, onChange, onDelete }) => {
  const [collapsed, setCollapsed] = useState(true);

  const updateSection = (key, value) => onChange({ ...section, [key]: value });
  const updateField = (idx, field) => {
    const fields = [...section.fields];
    fields[idx] = field;
    updateSection('fields', fields);
  };

  return (
    <Card className="border-l-4 border-l-primary/30" data-testid={`tmpl-section-${section.id}`}>
      <CardHeader className="py-3 px-4 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-semibold">{section.title}</CardTitle>
            <span className="text-xs text-muted-foreground">({section.fields?.length || 0} campos)</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </Button>
          </div>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="pt-0 px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Titulo</Label>
              <Input value={section.title} onChange={(e) => updateSection('title', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Descripcion</Label>
              <Input value={section.description || ''} onChange={(e) => updateSection('description', e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={section.repeatable} onCheckedChange={(v) => updateSection('repeatable', v)} />
              <Label className="text-xs">Repetible</Label>
            </div>
            {section.repeatable && (
              <div className="flex items-center gap-2">
                <Label className="text-xs">Max:</Label>
                <Input type="number" value={section.max_repeat} onChange={(e) => updateSection('max_repeat', parseInt(e.target.value) || 1)} className="h-7 w-16 text-sm" min="1" max="10" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            {(section.fields || []).map((field, idx) => (
              <TemplateFieldEditor
                key={field.id}
                field={field}
                onChange={(f) => updateField(idx, f)}
                onDelete={() => {
                  const fields = [...section.fields];
                  fields.splice(idx, 1);
                  updateSection('fields', fields);
                }}
              />
            ))}
          </div>
          <Button variant="outline" size="sm" className="w-full gap-1 text-xs" onClick={() => {
            updateSection('fields', [...(section.fields || []), {
              id: `field_${Date.now()}`, type: 'text', label: 'Nuevo campo', placeholder: '', required: false
            }]);
          }}>
            <Plus className="w-3 h-3" /> Anadir campo
          </Button>
        </CardContent>
      )}
    </Card>
  );
};

const TaskRow = ({ task, employees, adminBooks, onAssign, onComplete, onOpenPhaseDetail, showAssign, t }) => {
  const isAutoTask = task.type === 'assign_phases';
  const taskTitle = isAutoTask
    ? `${t('admin.taskAssignPhases')}: ${task.book_title || ''}`
    : task.title;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors" data-testid={`admin-task-${task.task_id}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isAutoTask ? 'bg-amber-100' : 'bg-blue-100'}`}>
        {isAutoTask ? <BookOpen className="w-4 h-4 text-amber-600" /> : <Briefcase className="w-4 h-4 text-blue-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{taskTitle}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{task.created_by_name} — {new Date(task.created_at).toLocaleDateString()}</span>
          {task.assigned_to_name && (
            <Badge variant="outline" className="text-[10px] h-5">{task.assigned_to_name}</Badge>
          )}
        </div>
        {task.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{task.description}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {showAssign && (
          <Select value={task.assigned_to || '_none'} onValueChange={(val) => onAssign(task.task_id, val === '_none' ? null : val)}>
            <SelectTrigger className="h-7 w-28 text-xs" data-testid={`task-assign-select-${task.task_id}`}>
              <SelectValue placeholder="Asignar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Sin asignar</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.user_id} value={emp.user_id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {isAutoTask && task.book_id && (
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
            const book = adminBooks.find(b => b.book_id === task.book_id);
            if (book) onOpenPhaseDetail(book);
          }} title={t('admin.assignResponsibles')} data-testid={`task-phases-btn-${task.task_id}`}>
            <Clock className="w-3.5 h-3.5" />
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onComplete(task.task_id)} data-testid={`task-complete-${task.task_id}`}>
          <CheckCircle2 className="w-4 h-4 text-green-500" />
        </Button>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [bookSearch, setBookSearch] = useState('');
  const [bookStatusFilter, setBookStatusFilter] = useState('all');
  const [adminBooks, setAdminBooks] = useState([]);
  // Books currently in ``preparacion_imprenta`` — fed into the dashboard
  // widget + PrintPreparationDialog.
  const [pendingPrintCount, setPendingPrintCount] = useState(0);
  const [printPrepDialogOpen, setPrintPrepDialogOpen] = useState(false);
  // Beta-mode waitlist widget. Count is fetched alongside the rest of
  // the admin data so the badge stays in sync without an extra round-trip.
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  // Admin tool — whitelist of IPs that bypass rate-limits during testing.
  const [whitelistDialogOpen, setWhitelistDialogOpen] = useState(false);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permSelections, setPermSelections] = useState([]);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [deleteBookDialogOpen, setDeleteBookDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showFormPassword, setShowFormPassword] = useState(false);
  const emptyForm = { first_name: '', last_name: '', email: '', phone: '', password: '', role: 'client', is_admin: false, permissions: [], default_phases: [] };
  const [formData, setFormData] = useState(emptyForm);
  const [editUserId, setEditUserId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [phaseConfigOpen, setPhaseConfigOpen] = useState(false);
  const [phaseConfig, setPhaseConfig] = useState({});
  const [phaseDetailOpen, setPhaseDetailOpen] = useState(false);
  const [phaseDetailBook, setPhaseDetailBook] = useState(null);
  const [phaseDetailData, setPhaseDetailData] = useState(null);
  
  // Template repository state
  const [templates, setTemplates] = useState([]);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [templateSubTab, setTemplateSubTab] = useState('forms');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [assignTemplateOpen, setAssignTemplateOpen] = useState(false);
  const [assignBookId, setAssignBookId] = useState(null);
  const [editingPhaseTime, setEditingPhaseTime] = useState(null);
  const [adminTasks, setAdminTasks] = useState([]);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', book_id: '', assigned_to: '' });

  // Cover templates state
  const [coverTemplates, setCoverTemplates] = useState([]);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [editingCover, setEditingCover] = useState(null);
  const [uploadingCoverPreview, setUploadingCoverPreview] = useState(false);
  const [coverEditorOpen, setCoverEditorOpen] = useState(false);
  const [coverEditorTemplate, setCoverEditorTemplate] = useState(null);
  const [coverCreateMode, setCoverCreateMode] = useState(null); // 'clean' | 'analyze'
  const [analyzingCover, setAnalyzingCover] = useState(false);
  const [deletingCoverId, setDeletingCoverId] = useState(null);

  // Staff (employees + admin accounts) live in the Employees tab; the
  // Clients tab and its counter only show real customer accounts.
  const clients = users.filter(u => u.role !== 'employee' && !u.is_admin);
  const employees = users.filter(u => u.role === 'employee' || u.is_admin);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error(t('admin.accessDenied'));
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes, booksRes, tasksRes, tmplRes, coverRes, waitlistRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/books`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/tasks`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/form-templates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/cover-templates`, { withCredentials: true }),
        // Waitlist is best-effort: a failure here shouldn't block the
        // rest of the admin panel from loading.
        axios.get(`${API_URL}/api/admin/waitlist`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setAdminBooks(booksRes.data);
      setAdminTasks(tasksRes.data);
      setTemplates(tmplRes.data);
      setCoverTemplates(coverRes.data);
      // Derive count of books awaiting print preparation for the widget.
      // (The detailed list is fetched on-demand when the dialog opens.)
      const pending = (booksRes.data || []).filter(b => b.status === 'preparacion_imprenta');
      setPendingPrintCount(pending.length);
      setWaitlistCount(waitlistRes.data?.count || 0);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('admin.noAdminPermissions'));
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetail = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setUserDetail(response.data);
      setUserDetailOpen(true);
    } catch (error) {
      toast.error(t('admin.noUsersFound'));
    }
  };

  const toggleAdmin = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/make-admin/${userId}`, {}, { withCredentials: true });
      toast.success(response.data.is_admin ? t('admin.userPromoted') : t('admin.adminRemoved'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.noUsersFound'));
    }
  };

  const changeRole = async (userItem) => {
    const newRole = userItem.role === 'employee' ? 'client' : 'employee';
    try {
      await axios.put(`${API_URL}/api/admin/users/${userItem.user_id}`, 
        { role: newRole },
        { withCredentials: true }
      );
      toast.success(newRole === 'employee' ? t('admin.convertedToEmployee') : t('admin.convertedToClient'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.noUsersFound'));
    }
  };

  const openPermissions = (userItem) => {
    setPermUser(userItem);
    setPermSelections(userItem.permissions || []);
    setPermDialogOpen(true);
  };

  const savePermissions = async () => {
    if (!permUser) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${permUser.user_id}`,
        { permissions: permSelections },
        { withCredentials: true }
      );
      toast.success(t('admin.permissionsSaved'));
      setPermDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('admin.noUsersFound'));
    }
  };

  const togglePermission = (perm) => {
    setPermSelections(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const confirmDelete = (userItem) => {
    setUserToDelete(userItem);
    setDeleteDialogOpen(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userToDelete.user_id}`, { withCredentials: true });
      toast.success(t('admin.userDeleted'));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const confirmDeleteBook = (book) => {
    setBookToDelete(book);
    setDeleteBookDialogOpen(true);
  };

  const deleteBook = async () => {
    if (!bookToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/admin/books/${bookToDelete.book_id}`, { withCredentials: true });
      toast.success(t('admin.bookDeleted'));
      setDeleteBookDialogOpen(false);
      setBookToDelete(null);
      if (userDetail) viewUserDetail(userDetail.user.user_id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const changeBookStatus = async (bookId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/books/${bookId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(t('admin.statusUpdated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const filteredAdminBooks = adminBooks.filter(b => {
    const matchSearch = !bookSearch || 
      b.title?.toLowerCase().includes(bookSearch.toLowerCase()) ||
      b.user_name?.toLowerCase().includes(bookSearch.toLowerCase()) ||
      b.user_email?.toLowerCase().includes(bookSearch.toLowerCase());
    const matchStatus = bookStatusFilter === 'all' || b.status === bookStatusFilter;
    return matchSearch && matchStatus;
  });

  const openCreateDialog = (role = 'client') => {
    setFormData({ ...emptyForm, role });
    setShowFormPassword(false);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (userItem) => {
    setEditUserId(userItem.user_id);
    setFormData({
      first_name: userItem.first_name || userItem.name?.split(' ')[0] || '',
      last_name: userItem.last_name || userItem.name?.split(' ').slice(1).join(' ') || '',
      email: userItem.email || '',
      phone: userItem.phone || '',
      password: '',
      role: userItem.role || 'client',
      is_admin: userItem.is_admin || false,
      permissions: userItem.permissions || [],
      default_phases: userItem.default_phases || [],
    });
    setShowFormPassword(false);
    setEditDialogOpen(true);
  };

  const createUser = async () => {
    if (!formData.first_name || !formData.email || !formData.password) {
      toast.error(t('admin.fillRequired'));
      return;
    }
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, formData, { withCredentials: true });
      toast.success(t('admin.userCreated'));
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setFormLoading(false);
    }
  };

  const updateUser = async () => {
    if (!editUserId || !formData.first_name || !formData.email) {
      toast.error(t('admin.fillRequired'));
      return;
    }
    setFormLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.password) delete payload.password;
      await axios.put(`${API_URL}/api/admin/users/${editUserId}`, payload, { withCredentials: true });
      toast.success(t('admin.userUpdated'));
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setFormLoading(false);
    }
  };

  const toggleFormPermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  // Phase config (global defaults)
  const openPhaseConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/phase-config`, { withCredentials: true });
      setPhaseConfig(res.data.phases || {});
      setPhaseConfigOpen(true);
    } catch (error) {
      toast.error('Error loading phase config');
    }
  };

  const savePhaseConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/phase-config`, { phases: phaseConfig }, { withCredentials: true });
      toast.success(t('admin.phaseConfigSaved'));
      setPhaseConfigOpen(false);
    } catch (error) {
      toast.error('Error');
    }
  };

  const applyPhaseConfigToAll = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/phase-config/apply-all`, {}, { withCredentials: true });
      toast.success(t('admin.phaseConfigApplied', { count: res.data.updated_books }));
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  // Phase detail per book
  const openPhaseDetail = async (book) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/books/${book.book_id}/phases`, { withCredentials: true });
      setPhaseDetailBook(book);
      setPhaseDetailData(res.data);
      setEditingPhaseTime(null);
      setPhaseDetailOpen(true);
    } catch (error) {
      toast.error('Error');
    }
  };

  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  const previewBookPDF = async (book) => {
    setPdfLoading(true);
    setPdfPreviewTitle(book.title || 'Libro');
    try {
      toast.info('Generando PDF de imprenta...');
      const res = await axios.get(`${API_URL}/api/books/${book.book_id}/download-pdf`, { 
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreviewUrl(url);
      setPdfPreviewOpen(true);
    } catch (error) {
      toast.error('Error al generar el PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdfFromPreview = () => {
    if (!pdfPreviewUrl) return;
    const link = document.createElement('a');
    link.href = pdfPreviewUrl;
    link.setAttribute('download', `${pdfPreviewTitle}_imprenta.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('PDF descargado');
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl('');
    }
  };

  // Template management functions
  const profileLabels = { infantil: 'Infantil', adolescente: 'Adolescente', adulto: 'Adulto', tercera_edad: 'Tercera edad' };

  const duplicateTemplate = async (templateId) => {
    try {
      await axios.post(`${API_URL}/api/admin/form-templates/${templateId}/duplicate`, {}, { withCredentials: true });
      toast.success('Plantilla duplicada');
      fetchData();
    } catch (e) { toast.error('Error al duplicar'); }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/form-templates/${templateId}`, { withCredentials: true });
      toast.success('Plantilla eliminada');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const setDefaultTemplate = async (templateId) => {
    try {
      await axios.post(`${API_URL}/api/admin/form-templates/${templateId}/set-default`, {}, { withCredentials: true });
      toast.success('Plantilla marcada como defecto');
      fetchData();
    } catch (e) { toast.error('Error'); }
  };

  const saveEditingTemplate = async () => {
    if (!editingTemplate) return;
    try {
      if (editingTemplate.template_id) {
        await axios.put(`${API_URL}/api/admin/form-templates/${editingTemplate.template_id}`, editingTemplate, { withCredentials: true });
        toast.success('Plantilla guardada');
      } else {
        await axios.post(`${API_URL}/api/admin/form-templates`, editingTemplate, { withCredentials: true });
        toast.success('Plantilla creada');
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      fetchData();
    } catch (e) { toast.error('Error al guardar'); }
  };

  const assignTemplateToBook = async (templateId) => {
    if (!assignBookId) return;
    try {
      await axios.post(`${API_URL}/api/admin/books/${assignBookId}/assign-template/${templateId}`, {}, { withCredentials: true });
      toast.success('Plantilla asignada al libro');
      setAssignTemplateOpen(false);
      setAssignBookId(null);
      fetchData();
    } catch (e) { toast.error('Error al asignar'); }
  };

  const filteredTemplates = templateFilter === 'all' 
    ? templates 
    : templates.filter(t => t.profile_type === templateFilter);

  const saveBookPhaseTime = async (bookId, phase, days) => {
    try {
      await axios.put(`${API_URL}/api/admin/books/${bookId}/phases`, { phase, standard_days: parseFloat(days) }, { withCredentials: true });
      toast.success(t('admin.phaseTimeUpdated'));
      const res = await axios.get(`${API_URL}/api/admin/books/${bookId}/phases`, { withCredentials: true });
      setPhaseDetailData(res.data);
      setEditingPhaseTime(null);
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const assignPhaseEmployee = async (bookId, phase, employeeId) => {
    try {
      await axios.put(`${API_URL}/api/admin/books/${bookId}/assign`, { phase, assigned_to: employeeId || null }, { withCredentials: true });
      toast.success(t('admin.assignmentSaved'));
      const res = await axios.get(`${API_URL}/api/admin/books/${bookId}/phases`, { withCredentials: true });
      setPhaseDetailData(res.data);
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const getTrackingBadge = (tracking) => {
    if (!tracking) return null;
    if (tracking.tracking === 'completed') return <Badge className="bg-green-100 text-green-700 text-xs">{t('admin.trackingCompleted')}</Badge>;
    if (tracking.tracking === 'delayed') return <Badge className="bg-red-100 text-red-700 text-xs">{t('admin.trackingDelayed')} ({tracking.delay_days}d)</Badge>;
    return <Badge className="bg-green-100 text-green-700 text-xs">{t('admin.trackingOnTrack')}</Badge>;
  };

  const completeAdminTask = async (taskId) => {
    try {
      await axios.put(`${API_URL}/api/admin/tasks/${taskId}/complete`, {}, { withCredentials: true });
      toast.success(t('admin.taskCompleted'));
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const assignTask = async (taskId, employeeId) => {
    try {
      await axios.put(`${API_URL}/api/admin/tasks/${taskId}/assign`, { assigned_to: employeeId || null }, { withCredentials: true });
      toast.success('Tarea asignada');
      fetchData();
    } catch (error) {
      toast.error('Error al asignar');
    }
  };

  const createManualTask = async () => {
    if (!newTask.title) { toast.error('El titulo es obligatorio'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/tasks`, {
        title: newTask.title,
        description: newTask.description,
        book_id: newTask.book_id || null,
        assigned_to: newTask.assigned_to || null,
      }, { withCredentials: true });
      toast.success('Tarea creada');
      setCreateTaskOpen(false);
      setNewTask({ title: '', description: '', book_id: '', assigned_to: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear tarea');
    }
  };

  const myTasks = adminTasks.filter(t => t.assigned_to === user?.user_id);
  const teamTasks = adminTasks.filter(t => t.assigned_to !== user?.user_id);

  const getDueDateDisplay = (book) => {
    if (!book.due_date) return null;
    const due = new Date(book.due_date);
    const now = new Date();
    const isOverdue = due < now;
    return (
      <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`} data-testid={`due-date-${book.book_id}`}>
        {due.toLocaleDateString()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground">{t('admin.title')}</h1>
                <p className="text-muted-foreground">{t('admin.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setWhitelistDialogOpen(true)}
                variant="outline" size="sm"
                className="gap-2"
                title="Gestionar IPs que saltan el rate-limit"
                data-testid="open-whitelist-btn"
              >
                <ShieldOff className="w-4 h-4" />
                <span className="hidden sm:inline">IPs sin límite</span>
              </Button>
              <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />{t('admin.refresh')}
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-8">
              <Card className="card-elevated">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-bold">{stats.total_clients ?? stats.total_users}</p>
                      <p className="text-xs text-muted-foreground truncate">{t('admin.clients')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-bold">{stats.total_employees || 0}</p>
                      <p className="text-xs text-muted-foreground truncate">{t('admin.employees')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-bold">{stats.total_books}</p>
                      <p className="text-xs text-muted-foreground truncate">{t('admin.booksCreated')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-bold">{stats.total_orders}</p>
                      <p className="text-xs text-muted-foreground truncate">{t('admin.totalOrders')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated" data-testid="revenue-stats-card">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Euro className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-bold" data-testid="revenue-month" title={`Total histórico: ${(stats.revenue_total_eur ?? 0).toFixed(2)}€`}>
                        {(stats.revenue_month_eur ?? 0).toFixed(2)}€
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{t('admin.monthlyRevenue')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-bold">+{stats.recent_users_7d}</p>
                      <p className="text-xs text-muted-foreground truncate">{t('admin.newRecent')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tasks Panels */}
          {adminTasks.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {/* My Tasks */}
              <Card className="card-elevated border-l-4 border-l-primary" data-testid="my-tasks-panel">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-primary" />
                      Mis Tareas ({myTasks.length})
                    </CardTitle>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setCreateTaskOpen(true)} data-testid="create-task-btn">
                      <Plus className="w-3 h-3" /> Nueva tarea
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {myTasks.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No tienes tareas asignadas</p>
                  ) : (
                    <div className="space-y-2">
                      {myTasks.map((task) => (
                        <TaskRow key={task.task_id} task={task} employees={employees} adminBooks={adminBooks} onAssign={assignTask} onComplete={completeAdminTask} onOpenPhaseDetail={openPhaseDetail} showAssign={true} t={t} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Tasks */}
              <Card className="card-elevated border-l-4 border-l-amber-500" data-testid="team-tasks-panel">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-amber-500" />
                    Tareas del Equipo ({teamTasks.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {teamTasks.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay tareas de equipo pendientes</p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {teamTasks.map((task) => (
                        <TaskRow key={task.task_id} task={task} employees={employees} adminBooks={adminBooks} onAssign={assignTask} onComplete={completeAdminTask} onOpenPhaseDetail={openPhaseDetail} showAssign={true} t={t} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {adminTasks.length === 0 && (
            <div className="flex justify-end mb-4">
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setCreateTaskOpen(true)} data-testid="create-task-btn-empty">
                <Plus className="w-3 h-3" /> Nueva tarea
              </Button>
            </div>
          )}

          {/* Print preparation widget — shows count of books in
              ``preparacion_imprenta`` and opens the dialog to review +
              approve them. Only renders when there's at least 1 pending. */}
          {pendingPrintCount > 0 && (
            <Card
              className="mb-4 border-l-4 border-l-teal-500 bg-teal-50/30 hover:bg-teal-50/60 transition-colors cursor-pointer"
              onClick={() => setPrintPrepDialogOpen(true)}
              data-testid="print-prep-widget"
            >
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Printer className="w-5 h-5 text-teal-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-teal-900 flex items-center gap-2">
                      <span className="text-base">🔵</span>
                      {pendingPrintCount} {pendingPrintCount === 1 ? 'libro esperando' : 'libros esperando'} preparación para imprenta
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Revisa la maquetación final, completa el checklist técnico y aprueba el envío a imprenta.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700 text-white gap-1"
                  onClick={(e) => { e.stopPropagation(); setPrintPrepDialogOpen(true); }}
                  data-testid="open-print-prep-btn"
                >
                  Revisar <ChevronRight className="w-3 h-3" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Beta-mode waitlist widget — shows how many people have
              asked for an invite while BETA_MODE is enabled, so the
              admin can export their emails and notify them at launch. */}
          {waitlistCount > 0 && (
            <Card
              className="mb-4 border-l-4 border-l-violet-500 bg-violet-50/30 hover:bg-violet-50/60 transition-colors cursor-pointer"
              onClick={() => setWaitlistDialogOpen(true)}
              data-testid="waitlist-widget"
            >
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <MailQuestion className="w-5 h-5 text-violet-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-violet-900 flex items-center gap-2">
                      <span className="text-base">📬</span>
                      {waitlistCount} {waitlistCount === 1 ? 'persona en lista de espera' : 'personas en lista de espera'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Han pedido aviso durante el modo beta. Exporta el CSV cuando vayas a abrir el registro general.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700 text-white gap-1"
                  onClick={(e) => { e.stopPropagation(); setWaitlistDialogOpen(true); }}
                  data-testid="open-waitlist-btn"
                >
                  Ver lista <ChevronRight className="w-3 h-3" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Tabs: Clientes / Empleados / Libros */}
          <Tabs defaultValue="clients" className="space-y-4" data-testid="admin-tabs">
            {/* Mobile: horizontal scroll preserves each tab's natural width so
                icon + label never collapse on top of each other (7 tabs in
                grid-cols-7 forced ~55px/tab on a 390px viewport).
                Desktop (md+): keep the original evenly-distributed grid. */}
            <TabsList
              className="flex md:grid w-full md:max-w-6xl md:grid-cols-9 gap-1 h-auto md:h-9 overflow-x-auto md:overflow-visible justify-start md:justify-center scrollbar-thin -mx-1 px-1 md:mx-0 md:px-1"
            >
              <TabsTrigger value="clients" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-clients">
                <UserCheck className="w-4 h-4" />
                {t('admin.clients')} ({clients.length})
              </TabsTrigger>
              <TabsTrigger value="employees" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-employees">
                <Briefcase className="w-4 h-4" />
                {t('admin.employees')} ({employees.length})
              </TabsTrigger>
              <TabsTrigger value="books" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-books">
                <BookOpen className="w-4 h-4" />
                {t('admin.books')} ({adminBooks.length})
              </TabsTrigger>
              <TabsTrigger value="billing" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-billing">
                <Receipt className="w-4 h-4" />
                Facturacion
              </TabsTrigger>
              <TabsTrigger value="payments" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-payments">
                <Euro className="w-4 h-4" />
                Pagos
              </TabsTrigger>
              <TabsTrigger value="costs" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-costs">
                <Sparkles className="w-4 h-4" />
                Costes Proyecto
              </TabsTrigger>
              <TabsTrigger value="templates" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-templates">
                <FileText className="w-4 h-4" />
                Plantillas
              </TabsTrigger>
              <TabsTrigger value="feedback" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-feedback">
                <MessageSquareHeart className="w-4 h-4" />
                Opiniones
              </TabsTrigger>
              <TabsTrigger value="help" className="gap-2 flex-shrink-0 md:flex-shrink" data-testid="tab-help">
                <HelpCircle className="w-4 h-4" />
                Ayuda
              </TabsTrigger>
            </TabsList>

            {/* Clients Tab */}
            <TabsContent value="feedback">
              <AdminFeedbackTab />
            </TabsContent>

            <TabsContent value="clients">
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="font-heading">{t('admin.clientManagement')}</CardTitle>
                      <CardDescription>{clients.length} {t('admin.registeredClients')}</CardDescription>
                    </div>
                    <Button onClick={() => openCreateDialog('client')} className="btn-primary gap-2" data-testid="create-client-btn">
                      <UserPlus className="w-4 h-4" />{t('admin.createClient')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <UserTable
                    users={clients}
                    searchTerm={clientSearch}
                    onSearch={setClientSearch}
                    onViewDetail={viewUserDetail}
                    onEditUser={openEditDialog}
                    onToggleAdmin={toggleAdmin}
                    onDelete={confirmDelete}
                    onChangeRole={changeRole}
                    onManagePermissions={openPermissions}
                    currentUserId={user?.user_id}
                    t={t}
                    showRoleSwitch={true}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Employees Tab */}
            <TabsContent value="employees">
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="font-heading">{t('admin.employeeManagement')}</CardTitle>
                      <CardDescription>{employees.length} {t('admin.registeredEmployees')}</CardDescription>
                    </div>
                    <Button onClick={() => openCreateDialog('employee')} className="btn-primary gap-2" data-testid="create-employee-btn">
                      <UserPlus className="w-4 h-4" />{t('admin.createEmployee')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {employees.length === 0 ? (
                    <div className="text-center py-12" data-testid="no-employees">
                      <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground font-medium">{t('admin.noEmployees')}</p>
                      <p className="text-sm text-muted-foreground mt-1">{t('admin.noEmployeesDesc')}</p>
                    </div>
                  ) : (
                    <UserTable
                      users={employees}
                      searchTerm={employeeSearch}
                      onSearch={setEmployeeSearch}
                      onViewDetail={viewUserDetail}
                      onEditUser={openEditDialog}
                      onToggleAdmin={toggleAdmin}
                      onDelete={confirmDelete}
                      onChangeRole={changeRole}
                      onManagePermissions={openPermissions}
                      currentUserId={user?.user_id}
                      t={t}
                      showRoleSwitch={true}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Books Tab */}
            <TabsContent value="books">
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="font-heading">{t('admin.bookStatusManagement')}</CardTitle>
                      <CardDescription>{adminBooks.length} {t('admin.totalBooks')}</CardDescription>
                    </div>
                    <Button onClick={openPhaseConfig} variant="outline" className="gap-2" data-testid="phase-config-btn">
                      <Timer className="w-4 h-4" />{t('admin.phaseConfig')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search and Filter */}
                  <div className="flex flex-col md:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={t('admin.searchBooks')}
                        value={bookSearch}
                        onChange={(e) => setBookSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-books"
                      />
                    </div>
                    <Select value={bookStatusFilter} onValueChange={setBookStatusFilter}>
                      <SelectTrigger className="w-full md:w-48" data-testid="filter-book-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('dashboard.filterAll')}</SelectItem>
                        {BOOK_STATUSES.map(s => (
                          <SelectItem key={s.key} value={s.key}>{t(`admin.bookStatus.${s.key}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Books Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">{t('dashboard.colTitle')}</th>
                          <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">{t('admin.owner')}</th>
                          <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">{t('dashboard.colStatus')}</th>
                          <th className="text-center py-3 px-3 font-medium text-muted-foreground text-sm">{t('admin.tracking')}</th>
                          <th className="text-center py-3 px-3 font-medium text-muted-foreground text-sm">Paginas</th>
                          <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">{t('admin.dueDate')}</th>
                          <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">Entrega estimada</th>
                          <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">{t('admin.responsible')}</th>
                          <th className="text-right py-3 px-3 font-medium text-muted-foreground text-sm">{t('admin.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAdminBooks.map((book) => {
                          const statusConf = BOOK_STATUSES.find(s => s.key === book.status) || { key: book.status, color: 'bg-gray-100 text-gray-700' };
                          return (
                            <tr key={book.book_id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`admin-book-${book.book_id}`}>
                              <td className="py-3 px-3">
                                <span className="font-medium">{book.title}</span>
                                <p className="text-xs text-muted-foreground">{book.profile?.recipient_name}</p>
                              </td>
                              <td className="py-3 px-3 text-sm">
                                <p>{book.user_name}</p>
                                <p className="text-xs text-muted-foreground">{book.user_email}</p>
                              </td>
                              <td className="py-3 px-3">
                                <Select 
                                  value={book.status} 
                                  onValueChange={(newStatus) => changeBookStatus(book.book_id, newStatus)}
                                >
                                  <SelectTrigger className="h-8 w-44" data-testid={`status-select-${book.book_id}`}>
                                    <Badge className={`${statusConf.color} text-xs`}>
                                      {t(`admin.bookStatus.${book.status}`, book.status)}
                                    </Badge>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {BOOK_STATUSES.map(s => (
                                      <SelectItem key={s.key} value={s.key}>
                                        <Badge className={`${s.color} text-xs`}>{t(`admin.bookStatus.${s.key}`)}</Badge>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-3 px-3 text-center">
                                {getTrackingBadge(book.tracking)}
                              </td>
                              <td className="py-3 px-3" data-testid={`page-progress-${book.book_id}`}>
                                {book.page_count != null ? (
                                  <div className="flex items-center gap-2 min-w-[110px]">
                                    <div className="flex-1">
                                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full ${book.page_count >= 200 ? 'bg-green-500' : book.page_count >= 120 ? 'bg-amber-500' : 'bg-red-400'}`}
                                          style={{ width: `${Math.min(100, (book.page_count / 240) * 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{book.page_count}/240</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="py-3 px-3">
                                {getDueDateDisplay(book) || <span className="text-xs text-muted-foreground">—</span>}
                              </td>
                              <td className="py-3 px-3 text-sm">
                                {book.estimated_delivery ? (
                                  <span className="text-primary font-medium text-xs">
                                    {new Date(book.estimated_delivery).toLocaleDateString()}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="py-3 px-3 text-sm">
                                {book.responsible_name ? (
                                  <Badge className="bg-primary/10 text-primary text-xs" data-testid={`responsible-${book.book_id}`}>{book.responsible_name}</Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">{t('admin.unassigned')}</span>
                                )}
                              </td>
                              <td className="py-3 px-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {book.generated_content && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                                      onClick={() => previewBookPDF(book)}
                                      disabled={pdfLoading}
                                      data-testid={`preview-pdf-${book.book_id}`}
                                      title="Previsualizar PDF de imprenta"
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => navigate(`/books/${book.book_id}`)}
                                    data-testid={`view-book-${book.book_id}`}
                                    title="Ver detalle del libro"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => openPhaseDetail(book)}
                                    data-testid={`phase-detail-${book.book_id}`}
                                    title={t('admin.viewPhases')}
                                  >
                                    <Clock className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => confirmDeleteBook(book)}
                                    data-testid={`admin-delete-book-${book.book_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {filteredAdminBooks.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">{t('admin.noBooksFound')}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>


            {/* Billing Tab */}
            <TabsContent value="billing">
              <AdminBillingPanel />
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <AdminPaymentsTab />
            </TabsContent>

            {/* Costs Tab */}
            <TabsContent value="costs">
              <AdminCostsPanel />
            </TabsContent>

            {/* Templates Tab */}
            <TabsContent value="templates">
              {/* Sub-tabs: Formularios / Notificaciones */}
              <div className="flex items-center gap-1 mb-4 bg-muted/50 p-1 rounded-lg w-fit">
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${templateSubTab === 'forms' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setTemplateSubTab('forms')}
                  data-testid="subtab-forms"
                >
                  <FileText className="w-4 h-4 inline mr-1.5" />
                  F. Colaboradores ({templates.length})
                </button>
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${templateSubTab === 'client_forms' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setTemplateSubTab('client_forms')}
                  data-testid="subtab-client-forms"
                >
                  <ClipboardList className="w-4 h-4 inline mr-1.5" />
                  F. Clientes
                </button>
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${templateSubTab === 'notifications' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setTemplateSubTab('notifications')}
                  data-testid="subtab-notifications"
                >
                  <Mail className="w-4 h-4 inline mr-1.5" />
                  Notificaciones
                </button>
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${templateSubTab === 'covers' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setTemplateSubTab('covers')}
                  data-testid="subtab-covers"
                >
                  <Package className="w-4 h-4 inline mr-1.5" />
                  Portadas ({coverTemplates.length})
                </button>
              </div>

              {templateSubTab === 'notifications' ? (
                <NotificationTemplatesPanel />
              ) : templateSubTab === 'client_forms' ? (
                <ClientFormTemplatesPanel />
              ) : templateSubTab === 'forms' ? (
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="font-heading">Formularios de Colaboradores</CardTitle>
                      <CardDescription>Plantillas de preguntas que ven los colaboradores al aportar historias. {templates.length} plantillas disponibles.</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <Select value={templateFilter} onValueChange={setTemplateFilter}>
                        <SelectTrigger className="w-44" data-testid="filter-template-profile">
                          <SelectValue placeholder="Filtrar por perfil" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los perfiles</SelectItem>
                          {Object.entries(profileLabels).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        className="btn-primary gap-2"
                        onClick={() => {
                          setEditingTemplate({
                            name: '',
                            profile_type: 'adulto',
                            is_default: false,
                            sections: [{
                              id: `section_${Date.now()}`,
                              title: 'Nueva seccion',
                              description: '',
                              repeatable: false,
                              max_repeat: 1,
                              fields: [{ id: `field_${Date.now()}`, type: 'text', label: 'Nuevo campo', placeholder: '', required: false }]
                            }]
                          });
                          setTemplateDialogOpen(true);
                        }}
                        data-testid="create-template-btn"
                      >
                        <Plus className="w-4 h-4" /> Nueva plantilla
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredTemplates.length === 0 ? (
                    <div className="text-center py-12" data-testid="no-templates">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground font-medium">No hay plantillas</p>
                      <p className="text-sm text-muted-foreground mt-1">Crea tu primera plantilla de formulario</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">Nombre</th>
                            <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">Perfil</th>
                            <th className="text-center py-3 px-3 font-medium text-muted-foreground text-sm">Secciones</th>
                            <th className="text-center py-3 px-3 font-medium text-muted-foreground text-sm">Campos</th>
                            <th className="text-center py-3 px-3 font-medium text-muted-foreground text-sm">Predeterminada</th>
                            <th className="text-left py-3 px-3 font-medium text-muted-foreground text-sm">Creada</th>
                            <th className="text-right py-3 px-3 font-medium text-muted-foreground text-sm">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTemplates.map((tmpl) => {
                            const totalFields = (tmpl.sections || []).reduce((sum, s) => sum + (s.fields?.length || 0), 0);
                            return (
                              <tr key={tmpl.template_id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`template-row-${tmpl.template_id}`}>
                                <td className="py-3 px-3">
                                  <span className="font-medium">{tmpl.name}</span>
                                </td>
                                <td className="py-3 px-3">
                                  <Badge variant="outline" className="text-xs">{profileLabels[tmpl.profile_type] || tmpl.profile_type}</Badge>
                                </td>
                                <td className="py-3 px-3 text-center">
                                  <Badge variant="secondary" className="text-xs">{tmpl.sections?.length || 0}</Badge>
                                </td>
                                <td className="py-3 px-3 text-center">
                                  <Badge variant="secondary" className="text-xs">{totalFields}</Badge>
                                </td>
                                <td className="py-3 px-3 text-center">
                                  {tmpl.is_default ? (
                                    <Badge className="bg-amber-100 text-amber-700 text-xs gap-1">
                                      <Star className="w-3 h-3" /> Defecto
                                    </Badge>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 text-xs text-muted-foreground"
                                      onClick={() => setDefaultTemplate(tmpl.template_id)}
                                      data-testid={`set-default-${tmpl.template_id}`}
                                    >
                                      Marcar defecto
                                    </Button>
                                  )}
                                </td>
                                <td className="py-3 px-3">
                                  <span className="text-xs text-muted-foreground">
                                    {tmpl.created_at ? new Date(tmpl.created_at).toLocaleDateString('es') : '—'}
                                  </span>
                                </td>
                                <td className="py-3 px-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        setEditingTemplate({ ...tmpl });
                                        setTemplateDialogOpen(true);
                                      }}
                                      data-testid={`edit-template-${tmpl.template_id}`}
                                      title="Editar"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => duplicateTemplate(tmpl.template_id)}
                                      data-testid={`duplicate-template-${tmpl.template_id}`}
                                      title="Duplicar"
                                    >
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => deleteTemplate(tmpl.template_id)}
                                      data-testid={`delete-template-${tmpl.template_id}`}
                                      title="Eliminar"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
              ) : (
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="font-heading">Gestion de Portadas</CardTitle>
                      <CardDescription>{coverTemplates.length} plantillas de portada disponibles</CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button className="btn-primary gap-2" data-testid="create-cover-btn">
                          <Plus className="w-4 h-4" /> Nueva portada
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72">
                        <DropdownMenuItem
                          className="flex flex-col items-start py-3 cursor-pointer"
                          onClick={() => {
                            setEditingCover({ name: '', template_key: 'retrato_dramatico', is_active: true, background_type: 'photo' });
                            setCoverDialogOpen(true);
                            setCoverCreateMode('clean');
                          }}
                          data-testid="create-cover-clean"
                        >
                          <span className="font-medium text-sm">Imagen de fondo limpia</span>
                          <span className="text-xs text-muted-foreground mt-0.5">Sube una imagen sin texto y añade los campos manualmente</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="flex flex-col items-start py-3 cursor-pointer"
                          onClick={() => setCoverCreateMode('analyze')}
                          data-testid="create-cover-analyze"
                        >
                          <span className="font-medium text-sm flex items-center gap-1.5">Ejemplo completo <Badge variant="outline" className="text-[10px] px-1.5 py-0">IA</Badge></span>
                          <span className="text-xs text-muted-foreground mt-0.5">Sube una portada con texto y la IA creara la plantilla automaticamente</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  {coverTemplates.length === 0 ? (
                    <div className="text-center py-12" data-testid="no-covers">
                      <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground font-medium">No hay portadas</p>
                      <p className="text-sm text-muted-foreground mt-1">Crea tu primera plantilla de portada</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="covers-grid">
                      {coverTemplates.map((cover) => (
                        <div key={cover.template_id} className="group border rounded-xl overflow-hidden hover:shadow-lg transition-all" data-testid={`cover-card-${cover.template_id}`}>
                          {/* Preview Image — uses CoverFrontPanel so the
                              admin grid matches every other surface
                              exactly (catalog, inline preview, full
                              spread). Single source of truth. */}
                          <div className="aspect-[3/4] bg-muted/30 relative overflow-hidden" style={{ containerType: 'inline-size' }}>
                            <CoverFrontPanel
                              template={cover}
                              fieldValues={{
                                protagonist_name: (cover.sample_texts || {}).protagonist_name || 'MANOLO',
                                subtitle: (cover.sample_texts || {}).subtitle || 'Un libro para',
                                title: (cover.sample_texts || {}).title || 'Las Aventuras de un Hombre Extraordinario',
                                branding: (cover.sample_texts || {}).branding || 'A GIFT OF MEMORIES',
                              }}
                            />
                            {/* Overlay on hover */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <label className="cursor-pointer bg-white/90 text-black rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-white transition-colors">
                                <Camera className="w-3 h-3 inline mr-1" />
                                {cover.preview_image_path ? 'Cambiar' : 'Subir'} imagen
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    setUploadingCoverPreview(true);
                                    try {
                                      const fd = new FormData();
                                      fd.append('file', file);
                                      await axios.post(`${API_URL}/api/admin/cover-templates/${cover.template_id}/upload-preview`, fd, {
                                        withCredentials: true,
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      toast.success('Imagen actualizada');
                                      fetchData();
                                    } catch {
                                      toast.error('Error al subir imagen');
                                    } finally {
                                      setUploadingCoverPreview(false);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </label>
                            </div>
                            {/* Active badge */}
                            {!cover.is_active && (
                              <div className="absolute top-2 left-2">
                                <Badge variant="secondary" className="text-xs bg-red-100 text-red-700">Inactiva</Badge>
                              </div>
                            )}
                          </div>
                          {/* Info */}
                          <div className="p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-sm truncate">{cover.name}</h4>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`cover-menu-${cover.template_id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setCoverEditorTemplate(cover);
                                    setCoverEditorOpen(true);
                                  }}>
                                    <Pencil className="w-4 h-4 mr-2" /> Editar diseno
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setEditingCover({ ...cover });
                                    setCoverDialogOpen(true);
                                  }}>
                                    <Settings className="w-4 h-4 mr-2" /> Renombrar
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={async () => {
                                    try {
                                      await axios.put(`${API_URL}/api/admin/cover-templates/${cover.template_id}`, 
                                        { is_active: !cover.is_active }, 
                                        { withCredentials: true }
                                      );
                                      toast.success(cover.is_active ? 'Portada desactivada' : 'Portada activada');
                                      fetchData();
                                    } catch { toast.error('Error'); }
                                  }}>
                                    {cover.is_active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                    {cover.is_active ? 'Desactivar' : 'Activar'}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onSelect={() => setDeletingCoverId(cover.template_id)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{cover.template_key}</Badge>
                              {cover.is_active && <Badge className="bg-green-100 text-green-700 text-xs">Activa</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {cover.created_at ? new Date(cover.created_at).toLocaleDateString('es') : '—'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              )}
            </TabsContent>

            <TabsContent value="help">
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <AdminHelpPanel />
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </main>

      {/* Print preparation review dialog (admin) */}
      <PrintPreparationDialog
        open={printPrepDialogOpen}
        onClose={() => setPrintPrepDialogOpen(false)}
        onApproved={() => {
          // Refresh the books list + the widget count after approval.
          fetchData?.();
        }}
      />

      {/* Beta waitlist dialog (admin) */}
      <WaitlistDialog
        open={waitlistDialogOpen}
        onClose={() => setWaitlistDialogOpen(false)}
      />

      {/* IP whitelist dialog (admin) */}
      <WhitelistedIpsDialog
        open={whitelistDialogOpen}
        onClose={() => setWhitelistDialogOpen(false)}
      />

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('admin.userDetails')}</DialogTitle>
          </DialogHeader>
          {userDetail && (
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {userDetail.user.picture ? (
                    <img src={userDetail.user.picture} alt="" className="w-16 h-16 rounded-full" />
                  ) : (
                    <span className="font-semibold text-primary text-2xl">
                      {userDetail.user.name?.charAt(0) || 'U'}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-xl font-semibold">{userDetail.user.name}</h3>
                  <p className="text-muted-foreground truncate">{userDetail.user.email}</p>
                  {userDetail.user.phone && <p className="text-sm">{userDetail.user.phone}</p>}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {userDetail.user.is_admin && <Badge className="bg-primary text-white">Admin</Badge>}
                    {userDetail.user.role === 'employee' ? (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">{t('admin.employee')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('admin.client')}</Badge>
                    )}
                    <Badge variant="outline">{userDetail.user.auth_provider}</Badge>
                  </div>
                  {userDetail.user.permissions?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {userDetail.user.permissions.map(p => (
                        <Badge key={p} variant="secondary" className="text-xs">{t(`admin.perm_${p}`)}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">{t('admin.books')} ({userDetail.books.length})</h4>
                {userDetail.books.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('admin.noBooks')}</p>
                ) : (
                  <div className="space-y-2">
                    {userDetail.books.map((book) => {
                      const statusConf = BOOK_STATUSES.find(s => s.key === book.status) || { key: book.status, color: 'bg-gray-100 text-gray-700' };
                      return (
                        <div 
                          key={book.book_id} 
                          className="p-3 border rounded-lg flex items-center justify-between hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => { setUserDetailOpen(false); navigate(`/books/${book.book_id}`); }}
                          data-testid={`user-book-${book.book_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">{book.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('bookDetail.for')} {book.profile?.recipient_name} 
                              {book.estimated_delivery && ` · Entrega: ${new Date(book.estimated_delivery).toLocaleDateString()}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Badge className={`${statusConf.color} text-xs`}>{t(`admin.bookStatus.${book.status}`, book.status)}</Badge>
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-medium mb-3">{t('admin.orders')} ({userDetail.orders.length})</h4>
                {userDetail.orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('admin.noOrders')}</p>
                ) : (
                  <div className="space-y-2">
                    {userDetail.orders.map((order) => (
                      <div key={order.order_id} className="p-3 border rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium">{order.book_title}</p>
                          <p className="text-xs text-muted-foreground">{order.order_id} • {order.total_amount?.toFixed(2)}€</p>
                        </div>
                        <Badge className={
                          order.status === 'paid' ? 'bg-green-100 text-green-700' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }>{order.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" />
              {t('admin.managePermissions')}
            </DialogTitle>
            <DialogDescription>
              {permUser?.name} — {permUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {AVAILABLE_PERMISSIONS.map(({ key, icon: Icon }) => (
              <div key={key} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                <Checkbox
                  id={`perm-${key}`}
                  checked={permSelections.includes(key)}
                  onCheckedChange={() => togglePermission(key)}
                  data-testid={`perm-${key}`}
                />
                <Icon className="w-4 h-4 text-muted-foreground" />
                <Label htmlFor={`perm-${key}`} className="flex-1 cursor-pointer">
                  <span className="font-medium">{t(`admin.perm_${key}`)}</span>
                  <span className="block text-xs text-muted-foreground">{t(`admin.permDesc_${key}`)}</span>
                </Label>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPermDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={savePermissions} className="btn-primary" data-testid="save-permissions">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />{t('admin.confirmDelete')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.confirmDeleteDesc')} <strong>{userToDelete?.name}</strong>?
              <br /><br />
              {t('admin.deleteWarning')}
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>{t('admin.deleteAccount')}</li>
                <li>{t('admin.deleteAllBooks')} ({userToDelete?.books_count})</li>
                <li>{t('admin.deleteAllOrders')} ({userToDelete?.orders_count})</li>
                <li>{t('admin.deleteContributions')}</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('admin.cancel')}</Button>
            <Button variant="destructive" onClick={deleteUser} data-testid="confirm-delete-user">{t('admin.confirmDelete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Book Confirmation Dialog */}
      <Dialog open={deleteBookDialogOpen} onOpenChange={setDeleteBookDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />{t('admin.deleteBook')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.confirmDeleteBookDesc')} <strong>{bookToDelete?.title}</strong>?
              <br /><br />
              {t('admin.deleteBookWarning')}
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>{t('admin.deleteBookData')}</li>
                <li>{t('admin.deleteBookContributors')}</li>
                <li>{t('admin.deleteBookContributions')}</li>
                <li>{t('admin.deleteBookOrders')}</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteBookDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={deleteBook} data-testid="confirm-delete-book">{t('admin.deleteBook')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              {formData.role === 'employee' ? t('admin.createEmployee') : t('admin.createClient')}
            </DialogTitle>
          </DialogHeader>
          <UserForm
            formData={formData}
            setFormData={setFormData}
            showPassword={showFormPassword}
            setShowPassword={setShowFormPassword}
            toggleFormPermission={toggleFormPermission}
            t={t}
            isCreate={true}
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={createUser} className="btn-primary" disabled={formLoading} data-testid="confirm-create-user">
              {formLoading ? t('common.loading') : t('admin.createUser')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              {t('admin.editUser')}
            </DialogTitle>
          </DialogHeader>
          <UserForm
            formData={formData}
            setFormData={setFormData}
            showPassword={showFormPassword}
            setShowPassword={setShowFormPassword}
            toggleFormPermission={toggleFormPermission}
            t={t}
            isCreate={false}
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={updateUser} className="btn-primary" disabled={formLoading} data-testid="confirm-edit-user">
              {formLoading ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />

      {/* Phase Config Dialog */}
      <Dialog open={phaseConfigOpen} onOpenChange={setPhaseConfigOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Timer className="w-5 h-5" />{t('admin.phaseConfig')}
            </DialogTitle>
            <DialogDescription>{t('admin.phaseConfigDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {BOOK_STATUSES.map((s, idx) => (
              <div key={s.key} className="flex items-center gap-3 p-3 rounded-lg border">
                <span className="text-sm font-medium text-muted-foreground w-6">{idx + 1}.</span>
                <Badge className={`${s.color} text-xs flex-shrink-0`}>{t(`admin.bookStatus.${s.key}`)}</Badge>
                <div className="flex-1" />
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    className="w-20 h-8 text-center text-sm"
                    value={phaseConfig[s.key] ?? ''}
                    onChange={(e) => setPhaseConfig(prev => ({ ...prev, [s.key]: parseFloat(e.target.value) || 0 }))}
                    data-testid={`phase-config-${s.key}`}
                  />
                  <span className="text-xs text-muted-foreground">{t('admin.days')}</span>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPhaseConfigOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="secondary" onClick={applyPhaseConfigToAll} data-testid="apply-phase-config-all">{t('admin.applyToAllBooks')}</Button>
            <Button onClick={savePhaseConfig} className="btn-primary" data-testid="save-phase-config">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase Detail Dialog (per book) */}
      <Dialog open={phaseDetailOpen} onOpenChange={setPhaseDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5" />{t('admin.phaseDetail')}
            </DialogTitle>
            <DialogDescription>
              {phaseDetailBook?.title} — {phaseDetailBook?.user_name}
            </DialogDescription>
          </DialogHeader>
          {phaseDetailData && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <span className="text-sm font-medium">{t('admin.currentPhase')}:</span>
                <Badge className={(BOOK_STATUSES.find(s => s.key === phaseDetailData.status) || {}).color || 'bg-gray-100 text-gray-700'}>
                  {t(`admin.bookStatus.${phaseDetailData.status}`, phaseDetailData.status)}
                </Badge>
                <div className="flex-1" />
                {getTrackingBadge(phaseDetailData.tracking)}
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-[28px_1fr_1fr_1fr_auto] gap-2 px-3 text-xs font-medium text-muted-foreground">
                <div />
                <div>{t('admin.phase')}</div>
                <div>{t('admin.responsible')}</div>
                <div>{t('admin.timing')}</div>
                <div />
              </div>

              <div className="space-y-2">
                {BOOK_STATUSES.map((s) => {
                  const phaseData = phaseDetailData.phases?.find(p => p.phase === s.key);
                  const isCurrent = phaseDetailData.status === s.key;
                  const isPast = phaseData?.completed_at;
                  const isEditing = editingPhaseTime === s.key;
                  const assignedEmployee = employees.find(e => e.user_id === phaseData?.assigned_to);

                  return (
                    <div key={s.key} className={`grid grid-cols-[28px_1fr_1fr_1fr_auto] gap-2 items-center p-3 rounded-lg border ${isCurrent ? 'border-primary bg-primary/5' : ''}`} data-testid={`phase-row-${s.key}`}>
                      <div className="flex items-center justify-center">
                        {isPast ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : isCurrent ? (
                          <AlertCircle className="w-5 h-5 text-primary" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                        )}
                      </div>
                      <div>
                        <Badge className={`${s.color} text-xs`}>{t(`admin.bookStatus.${s.key}`)}</Badge>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {phaseData?.started_at && new Date(phaseData.started_at).toLocaleDateString()}
                          {isPast && ` — ${new Date(phaseData.completed_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <div>
                        <Select
                          value={phaseData?.assigned_to || '_none'}
                          onValueChange={(val) => assignPhaseEmployee(phaseDetailBook.book_id, s.key, val === '_none' ? null : val)}
                        >
                          <SelectTrigger className="h-7 text-xs" data-testid={`phase-assign-${s.key}`}>
                            <SelectValue placeholder={t('admin.unassigned')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">{t('admin.unassigned')}</SelectItem>
                            {employees.map(emp => (
                              <SelectItem key={emp.user_id} value={emp.user_id}>{emp.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              className="w-16 h-7 text-center text-xs"
                              defaultValue={phaseData?.standard_days ?? phaseConfig[s.key] ?? ''}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveBookPhaseTime(phaseDetailBook.book_id, s.key, e.target.value);
                              }}
                              autoFocus
                              data-testid={`phase-edit-input-${s.key}`}
                            />
                            <Button size="sm" className="h-7 px-2 text-xs" onClick={(e) => {
                              const input = e.target.closest('div').querySelector('input');
                              saveBookPhaseTime(phaseDetailBook.book_id, s.key, input.value);
                            }}>{t('common.save')}</Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingPhaseTime(null)}>x</Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">
                              {phaseData?.standard_days ?? '—'} {t('admin.days')}
                            </span>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingPhaseTime(s.key)} data-testid={`phase-edit-btn-${s.key}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="text-xs">
                        {assignedEmployee && (
                          <span className="text-muted-foreground">{assignedEmployee.name?.split(' ')[0]}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={pdfPreviewOpen} onOpenChange={closePdfPreview}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <BookOpen className="w-5 h-5 text-amber-600" />
                  {pdfPreviewTitle}
                </DialogTitle>
                <DialogDescription className="text-xs mt-1">
                  PDF de imprenta — 15x21cm, encuadernacion rustica, 240 paginas
                </DialogDescription>
              </div>
              <Button 
                onClick={downloadPdfFromPreview} 
                className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4" /> Descargar PDF
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {pdfPreviewUrl && (
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-full border-0"
                title="Vista previa del PDF"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Editor Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={(open) => { if (!open) { setTemplateDialogOpen(false); setEditingTemplate(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingTemplate?.template_id ? 'Editar plantilla' : 'Nueva plantilla'}
            </DialogTitle>
            <DialogDescription>
              Define el nombre, perfil y estructura de secciones/campos de la plantilla.
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4 py-2" data-testid="template-editor-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Nombre de la plantilla *</Label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ej: Libro infantil personalizado"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium">Perfil</Label>
                  <Select value={editingTemplate.profile_type} onValueChange={(v) => setEditingTemplate(prev => ({ ...prev, profile_type: v }))}>
                    <SelectTrigger data-testid="template-profile-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(profileLabels).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingTemplate.is_default}
                  onCheckedChange={(v) => setEditingTemplate(prev => ({ ...prev, is_default: v }))}
                  data-testid="template-default-switch"
                />
                <Label className="text-sm">Marcar como plantilla predeterminada para este perfil</Label>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-3">Secciones del formulario</h4>
                <div className="space-y-3">
                  {(editingTemplate.sections || []).map((section, idx) => (
                    <TemplateSection
                      key={section.id}
                      section={section}
                      onChange={(s) => {
                        const sections = [...editingTemplate.sections];
                        sections[idx] = s;
                        setEditingTemplate(prev => ({ ...prev, sections }));
                      }}
                      onDelete={() => {
                        const sections = [...editingTemplate.sections];
                        sections.splice(idx, 1);
                        setEditingTemplate(prev => ({ ...prev, sections }));
                      }}
                    />
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full gap-2 mt-3"
                  onClick={() => {
                    const newId = `section_${Date.now()}`;
                    setEditingTemplate(prev => ({
                      ...prev,
                      sections: [...(prev.sections || []), {
                        id: newId, title: 'Nueva seccion', description: '', repeatable: false, max_repeat: 1,
                        fields: [{ id: `field_${Date.now()}`, type: 'text', label: 'Nuevo campo', placeholder: '', required: false }]
                      }]
                    }));
                  }}
                  data-testid="add-template-section-btn"
                >
                  <Plus className="w-4 h-4" /> Anadir seccion
                </Button>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setTemplateDialogOpen(false); setEditingTemplate(null); }}>Cancelar</Button>
            <Button
              className="btn-primary"
              onClick={saveEditingTemplate}
              disabled={!editingTemplate?.name}
              data-testid="save-template-btn"
            >
              {editingTemplate?.template_id ? 'Guardar cambios' : 'Crear plantilla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={createTaskOpen} onOpenChange={setCreateTaskOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus className="w-5 h-5" /> Nueva tarea
            </DialogTitle>
            <DialogDescription>Crea una tarea y asignala opcionalmente a un miembro del equipo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium">Titulo *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ej: Revisar maquetacion capitulo 3"
                data-testid="new-task-title"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Descripcion</Label>
              <Input
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detalles adicionales..."
                data-testid="new-task-description"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Libro relacionado (opcional)</Label>
              <Select value={newTask.book_id || '_none'} onValueChange={(v) => setNewTask(prev => ({ ...prev, book_id: v === '_none' ? '' : v }))}>
                <SelectTrigger data-testid="new-task-book">
                  <SelectValue placeholder="Sin libro" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sin libro</SelectItem>
                  {adminBooks.map(b => (
                    <SelectItem key={b.book_id} value={b.book_id}>{b.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Asignar a (opcional)</Label>
              <Select value={newTask.assigned_to || '_none'} onValueChange={(v) => setNewTask(prev => ({ ...prev, assigned_to: v === '_none' ? '' : v }))}>
                <SelectTrigger data-testid="new-task-assignee">
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sin asignar</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.user_id} value={emp.user_id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCreateTaskOpen(false)}>Cancelar</Button>
            <Button className="btn-primary" onClick={createManualTask} disabled={!newTask.title} data-testid="confirm-create-task">
              Crear tarea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cover Template Dialog */}
      <Dialog open={coverDialogOpen} onOpenChange={setCoverDialogOpen}>
        <DialogContent className="max-w-md" data-testid="cover-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCover?.template_id ? 'Editar portada' : 'Nueva portada'}
            </DialogTitle>
            <DialogDescription>
              {editingCover?.template_id ? 'Modifica los datos de la portada' : 'Crea una nueva plantilla de portada'}
            </DialogDescription>
          </DialogHeader>
          {editingCover && (
            <div className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={editingCover.name}
                  onChange={(e) => setEditingCover({ ...editingCover, name: e.target.value })}
                  placeholder="Ej: Retrato dramatico"
                  data-testid="cover-name-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingCover.is_active}
                  onCheckedChange={(v) => setEditingCover({ ...editingCover, is_active: v })}
                  data-testid="cover-active-switch"
                />
                <Label>Activa (visible para clientes)</Label>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCoverDialogOpen(false)}>Cancelar</Button>
            <Button
              className="btn-primary"
              disabled={!editingCover?.name}
              data-testid="save-cover-btn"
              onClick={async () => {
                try {
                  if (editingCover.template_id) {
                    await axios.put(`${API_URL}/api/admin/cover-templates/${editingCover.template_id}`, {
                      name: editingCover.name,
                      template_key: editingCover.template_key || 'retrato_dramatico',
                      is_active: editingCover.is_active,
                      background_type: editingCover.background_type || 'photo',
                    }, { withCredentials: true });
                    toast.success('Portada actualizada');
                  } else {
                    await axios.post(`${API_URL}/api/admin/cover-templates`, {
                      name: editingCover.name,
                      template_key: editingCover.template_key || 'retrato_dramatico',
                      is_active: editingCover.is_active,
                      background_type: editingCover.background_type || 'photo',
                    }, { withCredentials: true });
                    toast.success('Portada creada');
                  }
                  setCoverDialogOpen(false);
                  setEditingCover(null);
                  fetchData();
                } catch { toast.error('Error al guardar'); }
              }}
            >
              {editingCover?.template_id ? 'Guardar cambios' : 'Crear portada'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cover Template Visual Editor */}
      {coverEditorTemplate && (
        <CoverTemplateEditor
          template={coverEditorTemplate}
          open={coverEditorOpen}
          onClose={() => { setCoverEditorOpen(false); setCoverEditorTemplate(null); }}
          onSaved={fetchData}
        />
      )}

      {/* AI Cover Analysis Dialog */}
      <Dialog open={coverCreateMode === 'analyze'} onOpenChange={(v) => { if (!v && !analyzingCover) setCoverCreateMode(null); }}>
        <DialogContent className="max-w-md" data-testid="analyze-cover-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Crear portada desde ejemplo</DialogTitle>
            <DialogDescription>
              Sube una imagen de portada completa con texto. La IA analizara los tipos de letra, posiciones y colores, eliminara el texto y creara una plantilla editable.
            </DialogDescription>
          </DialogHeader>
          {analyzingCover ? (
            <div className="flex flex-col items-center py-8 gap-4" data-testid="analyzing-spinner">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium text-sm">Analizando portada con IA...</p>
                <p className="text-xs text-muted-foreground mt-1">Detectando fuentes, colores y posiciones. Generando fondo limpio.</p>
                <p className="text-xs text-muted-foreground mt-0.5">Esto puede tardar hasta 1 minuto.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-input rounded-xl p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('analyze-cover-input')?.click()}
                data-testid="analyze-upload-area"
              >
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm font-medium">Haz clic para subir el archivo de ejemplo</p>
                <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP o PDF. Maximo 15MB.</p>
                <input
                  id="analyze-cover-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setAnalyzingCover(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await axios.post(`${API_URL}/api/admin/cover-templates/analyze-cover`, fd, {
                        withCredentials: true,
                        headers: { 'Content-Type': 'multipart/form-data' },
                        timeout: 120000,
                      });
                      toast.success('Plantilla creada con IA');
                      setCoverCreateMode(null);
                      await fetchData();
                      // Open editor for fine-tuning
                      setCoverEditorTemplate(res.data);
                      setCoverEditorOpen(true);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Error al analizar la portada');
                    } finally {
                      setAnalyzingCover(false);
                      e.target.value = '';
                    }
                  }}
                  data-testid="analyze-cover-input"
                />
              </div>
              <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground space-y-1.5">
                <p className="font-medium text-foreground text-sm">¿Que hara la IA?</p>
                <p>1. Detectara los tipos de letra, colores y posiciones del texto</p>
                <p>2. Generara una imagen de fondo limpia (sin texto)</p>
                <p>3. Creara la plantilla con los campos configurados</p>
                <p>4. Abrira el editor para que puedas ajustar los detalles</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Cover Confirmation */}
      <AlertDialog open={!!deletingCoverId} onOpenChange={(v) => { if (!v) setDeletingCoverId(null); }}>
        <AlertDialogContent data-testid="delete-cover-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta portada?</AlertDialogTitle>
            <AlertDialogDescription>
              Los proyectos que ya tengan esta portada asignada no se veran afectados. Se conservara su diseno actual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cover-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-cover-confirm-btn"
              onClick={async () => {
                try {
                  await axios.delete(`${API_URL}/api/admin/cover-templates/${deletingCoverId}`, { withCredentials: true });
                  toast.success('Portada eliminada');
                  setDeletingCoverId(null);
                  fetchData();
                } catch { toast.error('Error al eliminar'); }
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPanel;
